import { useEffect, useRef, useState } from 'react'
import { serversApi } from '../../api'
import { inputClass, useSubmit } from './dialogShared'
import type { EdgeConfigResult, EdgeDevice, EdgeLine, EdgeMachine, EdgeTag } from '../../types'

// 라인·설비 추가/수정, 설비 Operation·Cycle 설정 다이얼로그 — 저장은 엣지 Parameter.xml 로 바로 간다 (EM 은 저장하지 않음)

export function Dialog({ title, wide, onClose, children, footer }: {
  title: string
  wide?: boolean
  onClose: () => void
  children: React.ReactNode
  footer: React.ReactNode
}) {
  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onMouseDown={onClose}>
      <div className={`bg-surface rounded-xl p-6 w-full ${wide ? 'max-w-xl' : 'max-w-md'} shadow-xl max-h-[90vh] overflow-y-auto`}
        onMouseDown={e => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="space-y-3">{children}</div>
        {footer}
      </div>
    </div>
  )
}

export function Label({ text, required, children }: { text: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-500 mb-1">{text}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  )
}

export function Footer({ saving, error, disabled, onClose, onSubmit }: {
  saving: boolean
  error: string | null
  disabled: boolean
  onClose: () => void
  onSubmit: () => void
}) {
  return (
    <>
      {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} disabled={saving}
          className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">
          취소
        </button>
        <button onClick={onSubmit} disabled={saving || disabled}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
          {saving ? '저장 중…' : '저장'}
        </button>
      </div>
    </>
  )
}

// line 이 있으면 수정, 없으면 추가
export function LineDialog({ serverId, line, onClose, onDone }: {
  serverId: string
  line?: EdgeLine
  onClose: () => void
  onDone: (result: EdgeConfigResult) => void
}) {
  const [name, setName] = useState(line?.name ?? '')
  const [description, setDescription] = useState(line?.description ?? '')
  const { saving, error, submit } = useSubmit(onDone)

  const save = () => {
    const body = { name: name.trim(), description: description.trim() || null }
    submit(() => (line ? serversApi.updateLine(serverId, line.id, body) : serversApi.createLine(serverId, body)))
  }

  return (
    <Dialog title={line ? '라인 수정' : '라인 추가'} onClose={onClose}
      footer={<Footer saving={saving} error={error} disabled={!name.trim()} onClose={onClose} onSubmit={save} />}>
      <Label text="라인 이름" required>
        <input value={name} onChange={e => setName(e.target.value)} autoFocus className={inputClass} />
      </Label>
      <Label text="설명">
        <input value={description} onChange={e => setDescription(e.target.value)} className={inputClass} />
      </Label>
    </Dialog>
  )
}

// machine 이 있으면 수정(설비코드·라인 고정), 없으면 lineId 라인에 추가
export function MachineDialog({ serverId, line, machine, onClose, onDone }: {
  serverId: string
  line: EdgeLine
  machine?: EdgeMachine
  onClose: () => void
  onDone: (result: EdgeConfigResult) => void
}) {
  const [code, setCode] = useState(machine?.code ?? '')
  const [name, setName] = useState(machine?.name ?? '')
  const [description, setDescription] = useState(machine?.description ?? '')
  const { saving, error, submit } = useSubmit(onDone)

  const save = () => {
    const body = {
      lineId: line.id,
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || null,
    }
    submit(() => (machine ? serversApi.updateMachine(serverId, body) : serversApi.createMachine(serverId, body)))
  }

  return (
    <Dialog title={machine ? '설비 수정' : '설비 추가'} onClose={onClose}
      footer={<Footer saving={saving} error={error} disabled={!code.trim() || !name.trim()} onClose={onClose} onSubmit={save} />}>
      <Label text="라인">
        <input value={line.name || line.code} disabled className={inputClass} />
      </Label>
      <Label text="설비코드" required>
        {/* 설비코드는 설비 로그·MES 의 키라 만든 뒤에는 바꾸지 않는다 */}
        <input value={code} onChange={e => setCode(e.target.value)} disabled={!!machine} autoFocus={!machine}
          className={`${inputClass} font-mono`} />
        {machine && <span className="block text-[11px] text-gray-400 mt-1">설비코드는 로그·MES 기준이라 바꿀 수 없습니다.</span>}
      </Label>
      <Label text="설비명" required>
        <input value={name} onChange={e => setName(e.target.value)} autoFocus={!!machine} className={inputClass} />
      </Label>
      <Label text="설명">
        <input value={description} onChange={e => setDescription(e.target.value)} className={inputClass} />
      </Label>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Operation · Cycle
// ═══════════════════════════════════════════════════════════════════════

// 묶음 입력 (label 로 감싸면 안의 첫 입력칸으로 포커스가 튀어서 div 로)
function Group({ text, required, hint, children }: { text: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="block text-xs text-gray-500 mb-1">{text}{required && <span className="text-red-500"> *</span>}</span>
      {children}
      {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
    </div>
  )
}

// 태그 하나 고르기 — 디바이스 → 태그 두 단계 선택
function TagSelect({ devices, value, onChange }: {
  devices: EdgeDevice[] | undefined
  value: string | null
  onChange: (tagId: string | null) => void
}) {
  const ownerOf = (tagId: string | null) => devices?.find(d => d.tags.some(t => t.id === tagId))?.code ?? ''
  const [deviceCode, setDeviceCode] = useState(() => ownerOf(value) || devices?.[0]?.code || '')

  if (!devices) return <p className="text-xs text-amber-600">디바이스 목록을 받지 못해 태그를 고를 수 없습니다.</p>

  const device = devices.find(d => d.code === deviceCode)
  return (
    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2">
      <select value={deviceCode} onChange={e => { setDeviceCode(e.target.value); onChange(null) }} className={inputClass}>
        {devices.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
      </select>
      <select value={value ?? ''} onChange={e => onChange(e.target.value || null)} className={`${inputClass} font-mono`}>
        <option value="">태그 선택</option>
        {device?.tags.map(t => (
          <option key={t.id} value={t.id}>{t.address || t.code}{t.description ? ` · ${t.description}` : ''}</option>
        ))}
      </select>
    </div>
  )
}

function MesFields({ checked, onChecked, code, onCode }: {
  checked: boolean
  onChecked: (v: boolean) => void
  code?: string
  onCode?: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <label className="flex items-center gap-2 text-sm text-gray-700 shrink-0">
        <input type="checkbox" checked={checked} onChange={e => onChecked(e.target.checked)} className="w-4 h-4 accent-blue-600" />
        MES 등록
      </label>
      {onCode && checked && (
        <input value={code} onChange={e => onCode(e.target.value)} placeholder="MES 코드" className={`${inputClass} font-mono`} />
      )}
    </div>
  )
}

const OPERATION_TYPES = [
  { value: 'NONE', label: '사용 안 함' },
  { value: 'CounterDetective', label: '카운터 신호 감지' },
]

// 가동 감지 — 카운터 태그 값이 기준 시간 안에 바뀌면 가동, 그보다 오래 안 바뀌면 정지 (엣지 MachineOperationUnit)
export function OperationDialog({ serverId, machine, devices, onClose, onDone }: {
  serverId: string
  machine: EdgeMachine
  devices: EdgeDevice[] | undefined
  onClose: () => void
  onDone: (result: EdgeConfigResult) => void
}) {
  const op = machine.operation
  const [type, setType] = useState(op?.type ?? 'NONE')
  const [tagId, setTagId] = useState<string | null>(op?.tagId ?? null)
  const [runValue, setRunValue] = useState(op?.runStateValue ?? '')
  const [stopValue, setStopValue] = useState(op?.stopStateValue ?? '')
  const [ignoreValue, setIgnoreValue] = useState(op?.ignoreTagValue ?? '')
  const [criteria, setCriteria] = useState(String(op?.criteria || 300000))
  const [mes, setMes] = useState(op?.mesRegisterable ?? false)
  const [code, setCode] = useState(op?.code ?? '')
  const { saving, error, submit } = useSubmit(onDone)

  const on = type !== 'NONE'
  const ms = Number(criteria)
  const valid = !on || (!!tagId && !!runValue && !!stopValue && Number.isInteger(ms) && ms > 0 && (!mes || !!code.trim()))

  const save = () => submit(() => serversApi.updateMachineOperation(serverId, machine.code, {
    type,
    tagId: on ? tagId : null,
    code: on ? code.trim() || null : null,
    mesRegisterable: on && mes,
    runStateValue: on ? runValue : null,
    stopStateValue: on ? stopValue : null,
    ignoreTagValue: on ? ignoreValue || null : null,
    criteria: on ? ms : 0,
  }))

  return (
    <Dialog title={`Operation — ${machine.name}`} wide onClose={onClose}
      footer={<Footer saving={saving} error={error} disabled={!valid} onClose={onClose} onSubmit={save} />}>
      <Label text="감지 방식">
        <select value={type} onChange={e => setType(e.target.value)} className={inputClass}>
          {OPERATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </Label>
      {on && (
        <>
          <Group text="카운터 태그" required hint="이 태그 값이 기준 시간 안에 바뀌면 가동, 그보다 오래 안 바뀌면 정지로 봅니다.">
            <TagSelect devices={devices} value={tagId} onChange={setTagId} />
          </Group>
          <div className="grid grid-cols-2 gap-3">
            <Label text="가동 값" required>
              <input value={runValue} onChange={e => setRunValue(e.target.value)} className={`${inputClass} font-mono`} />
            </Label>
            <Label text="정지 값" required>
              <input value={stopValue} onChange={e => setStopValue(e.target.value)} className={`${inputClass} font-mono`} />
            </Label>
            <Label text="무시 값">
              <input value={ignoreValue} onChange={e => setIgnoreValue(e.target.value)} className={`${inputClass} font-mono`} />
            </Label>
            <Label text="기준 시간 (ms)" required>
              <input type="number" min={1} step={1000} value={criteria} onChange={e => setCriteria(e.target.value)} className={inputClass} />
            </Label>
          </div>
          <MesFields checked={mes} onChecked={setMes} code={code} onCode={setCode} />
        </>
      )}
    </Dialog>
  )
}

const CYCLE_TYPES = [
  { value: 'None', label: '사용 안 함' },
  { value: 'SingleChangeDetective', label: '단일 신호 감지' },
  { value: 'StartEndChangeDetective', label: '시작/종료 신호 감지' },
]

// 시작/종료 조건 하나 — 태그 + (값 일치 | 값 변경 시)
function CycleCondition({ label, devices, tagId, onTag, value, onValue, onChange, onOnChange }: {
  label: string
  devices: EdgeDevice[] | undefined
  tagId: string | null
  onTag: (v: string | null) => void
  value: string
  onValue: (v: string) => void
  onChange: boolean
  onOnChange: (v: boolean) => void
}) {
  return (
    <Group text={`${label} 태그`} required>
      <TagSelect devices={devices} value={tagId} onChange={onTag} />
      <div className="flex items-center gap-3 mt-2">
        <select value={onChange ? 'change' : 'equals'} onChange={e => onOnChange(e.target.value === 'change')}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm shrink-0">
          <option value="equals">값이 같을 때</option>
          <option value="change">값이 바뀔 때</option>
        </select>
        {!onChange && (
          <input value={value} onChange={e => onValue(e.target.value)} placeholder={`${label} 값`} className={`${inputClass} font-mono`} />
        )}
      </div>
    </Group>
  )
}

// 사이클 — 시작(단일) 또는 시작·종료 태그 조건 (엣지 MachineCycleUnit). 저장된 사이클은 태그를 코드로 가리켜 Id 로 바꿔 쓴다
export function CycleDialog({ serverId, machine, devices, onClose, onDone }: {
  serverId: string
  machine: EdgeMachine
  devices: EdgeDevice[] | undefined
  onClose: () => void
  onDone: (result: EdgeConfigResult) => void
}) {
  const cy = machine.cycle
  const idOf = (code: string | null) =>
    (code && devices?.flatMap(d => d.tags).find((t: EdgeTag) => t.code === code)?.id) || null

  const [type, setType] = useState(cy?.cycleType ?? 'None')
  const [startTag, setStartTag] = useState<string | null>(() => idOf(cy?.startTagCode ?? null))
  const [startValue, setStartValue] = useState(cy?.startTagValue ?? '')
  const [startChange, setStartChange] = useState(cy?.startValueChangeDetective ?? false)
  const [endTag, setEndTag] = useState<string | null>(() => idOf(cy?.endTagCode ?? null))
  const [endValue, setEndValue] = useState(cy?.endTagValue ?? '')
  const [endChange, setEndChange] = useState(cy?.endValueChangeDetective ?? false)
  const [mes, setMes] = useState(cy?.mesRegisterable ?? false)
  const { saving, error, submit } = useSubmit(onDone)

  const on = type !== 'None'
  const needEnd = type === 'StartEndChangeDetective'
  const valid = !on || (
    !!startTag && (startChange || !!startValue)
    && (!needEnd || (!!endTag && (endChange || !!endValue))))

  const save = () => submit(() => serversApi.updateMachineCycle(serverId, machine.code, {
    cycleType: type,
    mesRegisterable: on && mes,
    startTagId: on ? startTag : null,
    startTagValue: on && !startChange ? startValue : null,
    startValueChangeDetective: on && startChange,
    endTagId: needEnd ? endTag : null,
    endTagValue: needEnd && !endChange ? endValue : null,
    endValueChangeDetective: needEnd && endChange,
  }))

  return (
    <Dialog title={`Cycle — ${machine.name}`} wide onClose={onClose}
      footer={<Footer saving={saving} error={error} disabled={!valid} onClose={onClose} onSubmit={save} />}>
      <Label text="감지 방식">
        <select value={type} onChange={e => setType(e.target.value)} className={inputClass}>
          {CYCLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </Label>
      {on && (
        <>
          <CycleCondition label="시작" devices={devices} tagId={startTag} onTag={setStartTag}
            value={startValue} onValue={setStartValue} onChange={startChange} onOnChange={setStartChange} />
          {needEnd && (
            <CycleCondition label="종료" devices={devices} tagId={endTag} onTag={setEndTag}
              value={endValue} onValue={setEndValue} onChange={endChange} onOnChange={setEndChange} />
          )}
          <MesFields checked={mes} onChecked={setMes} />
        </>
      )}
    </Dialog>
  )
}
